import { Route, Routes, useParams } from "react-router-dom-v5-compat";
import "./App.scss";
import type {
  BookedFlightItineraryWithDepartureTime,
  DisruptionProtectionAncillaryContract,
} from "@b2bportal/air-booking-api";
import type { Airport } from "@b2bportal/air-disruption-api";
import { LoadingScreen } from "@b2bportal/core-trips";
import type { AppDispatch } from "@b2bportal/core-types";
import { PATH_DISRUPTION_EXERCISE } from "@b2bportal/core-utilities";
import { ClientName } from "@hopper-b2b/types";
import { getEnvVariables } from "@hopper-b2b/utilities";
import { DisruptionUniversalExercise } from "@tenant/disruption";
import dayjs from "dayjs";
import {
  disruptionExerciseFlags,
  DisruptionExerciseProgress,
  setDisruptionExerciseProgress,
} from "./features/exercise";
import { useDispatch } from "react-redux";
import { useCallback, useMemo, useState } from "react";
import type { FetchItineraryThunk, FetchTripDetailsThunk } from "./types";

export interface DisruptionAppProps {
  disruptedFlight?: BookedFlightItineraryWithDepartureTime;
  onContactSupport?: () => void;
  handleExerciseCompleted?: () => void;
  displayExternalLinkAsModal: boolean;
  airports: { [key: string]: Airport };
  disruptionSelfServeRebook?: boolean;
  cancelFlowAction?: () => void;
  /** Adds the legal terms bullet to the refund-review information card. Defaults to false. */
  showTermsAndConditionsBullet?: boolean;
  /** Shows localized phone support in the refund-review footer. Defaults to false. */
  showSupportPhone?: boolean;
  fetchItinerary: FetchItineraryThunk;
  fetchTripDetails: FetchTripDetailsThunk;
}

export const DisruptionApp = (props: DisruptionAppProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [disruptionItinerary, setDisruptionItinerary] =
    useState<BookedFlightItineraryWithDepartureTime>();
  const params = useParams<{ reservationId: string }>();

  const handleError = useCallback(() => {
    // Show customer support when reservationId not available
    props.onContactSupport?.();
  }, [props]);

  const handleResponse = useCallback(
    (disruptionProtection?: DisruptionProtectionAncillaryContract) => {
      const hasDisruptionProtection = !!disruptionProtection;
      const { isDisruptionProtectionExercisable, isEligibleToBookReturn } =
        disruptionExerciseFlags(disruptionProtection);
      if (isDisruptionProtectionExercisable || isEligibleToBookReturn) {
        // use can exercise (refund/rebook) or (book return) the we start from landing page
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.LandingPage)
        );
      } else if (hasDisruptionProtection) {
        // user attached disruption but not eligible to rebook
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.NotEligible)
        );
      } else {
        // disruption not attached
        handleError();
      }
    },
    [dispatch, handleError]
  );

  useMemo(() => {
    if (props.disruptedFlight != null) {
      setDisruptionItinerary(props.disruptedFlight);
      handleResponse(props.disruptedFlight.ancillaries.disruptionProtection);
    } else if (params.reservationId != null) {
      // Load the flight itinerary
      Promise.resolve(
        dispatch(
          props.fetchItinerary({
            itineraryId: params.reservationId,
            referenceDateTime: dayjs().format(),
          })
        )
      )
        .then((result) => {
          const itin = result?.data;
          if (itin == null) {
            handleError();
            return;
          }
          setDisruptionItinerary(itin.itinerary);
          handleResponse(itin.itinerary.ancillaries.disruptionProtection);
        })
        .catch(() => {
          handleError();
        });
    } else {
      handleError();
    }
  }, []);

  if (!disruptionItinerary) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route
        path={PATH_DISRUPTION_EXERCISE} // this would only be `/exercise`
        element={
          <DisruptionUniversalExercise
            disruptedFlight={disruptionItinerary}
            onContactSupport={props.onContactSupport}
            displayExternalLinkAsModal={props.displayExternalLinkAsModal}
            airports={props.airports}
            handleExerciseCompleted={props.handleExerciseCompleted}
            disruptionSelfServeRebook={props.disruptionSelfServeRebook}
            showTermsAndConditionsBullet={props.showTermsAndConditionsBullet}
            showSupportPhone={props.showSupportPhone}
            cancelFlowAction={props.cancelFlowAction}
            fetchTripDetails={props.fetchTripDetails}
          />
        }
      />
      {/*
      Specific route for uber
      TODO: Will remove later once Uber is fully integrated with core (https://hopper-jira.atlassian.net/browse/DS-992)
      */}
      {getEnvVariables("clientName") === ClientName.UBER && (
        <Route
          path={"/trips/disruption/exercise"}
          element={
            <DisruptionUniversalExercise
              disruptedFlight={disruptionItinerary}
              onContactSupport={props.onContactSupport}
              displayExternalLinkAsModal={props.displayExternalLinkAsModal}
              airports={props.airports}
              handleExerciseCompleted={props.handleExerciseCompleted}
              disruptionSelfServeRebook={props.disruptionSelfServeRebook}
              showTermsAndConditionsBullet={props.showTermsAndConditionsBullet}
              showSupportPhone={props.showSupportPhone}
              cancelFlowAction={props.cancelFlowAction}
              fetchTripDetails={props.fetchTripDetails}
            />
          }
        />
      )}
    </Routes>
  );
};

export default DisruptionApp;
